import { useState } from 'react'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { getV1MeRetrieveQueryOptions, useV1RspoInstitutionsList } from '@/api/client'
import type { RspoInstitutionList } from '@/api/model'
import { PageHeader } from '@/components/page-header'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DataTable, type Column, type StateOption } from '@/components/data-table'
import { StatusBadge } from '@/components/status-badge'
import { queryHasHttpError } from '@/lib/query-state'
import { rspoVoivodeshipOptions } from '@/lib/rspo-voivodeships'
import { zsckrMarker } from '@/lib/rspo-zsckr'
import { Wheat } from 'lucide-react'

const ALL = '__all__'
const PAGE_SIZE = 25

type RspoPill = 'all' | 'root' | 'complex' | 'zsckr' | 'in-program'

const pillOptions: StateOption<RspoPill>[] = [
  { value: 'all', label: 'Wszystkie' },
  { value: 'root', label: 'Tylko rooty' },
  { value: 'complex', label: 'Jednostki złożone' },
  { value: 'zsckr', label: 'Tylko ZSCKR' },
  { value: 'in-program', label: 'W programie' },
]

export function rspoEmptyMessage(
  search: string,
  pill: RspoPill,
  hasVoivodeshipFilter: boolean,
  hasTypeFilter: boolean,
): string {
  const filtering = search.trim() !== '' || pill !== 'all' || hasVoivodeshipFilter || hasTypeFilter
  if (!filtering) return 'Rejestr RSPO jest pusty. Uruchom import komendą import_rspo.'
  if (pill === 'zsckr' && search.trim() === '' && !hasVoivodeshipFilter && !hasTypeFilter) {
    return 'Brak placówek oznaczonych jako ZSCKR. Przelicz flagę komendą backfill_zsckr.'
  }
  if (pill === 'in-program' && search.trim() === '' && !hasVoivodeshipFilter && !hasTypeFilter) {
    return 'Żadna placówka nie jest jeszcze powiązana ze szkołą w programie.'
  }
  return 'Brak placówek spełniających kryteria. Wyczyść wyszukiwanie i filtry.'
}

function hierarchySubtitle(row: RspoInstitutionList): string {
  const sp = row.structure_position
  const parent = row.parent_name
  if (sp && parent) return `${sp} · ${parent}`
  if (sp) return sp
  if (parent) return `jednostka podrzędna · ${parent}`
  return ''
}

export const Route = createFileRoute('/_panel/katalog-rspo/')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    if (me.status === 200 && me.data.role === 'teacher') {
      throw redirect({ to: '/' })
    }
  },
  component: RspoCatalogPage,
})

function RspoCatalogPage() {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [pill, setPill] = useState<RspoPill>('all')
  const [voivodeship, setVoivodeship] = useState<string>(ALL)
  const [type, setType] = useState('')
  const [ordering, setOrdering] = useState('name')

  const queryParams = {
    search: search || undefined,
    address_voivodeship: voivodeship === ALL ? undefined : voivodeship,
    type: type || undefined,
    is_root: pill === 'root' ? true : undefined,
    structure_position: pill === 'complex' ? 'jednostka złożona' : undefined,
    is_zsckr: pill === 'zsckr' ? true : undefined,
    in_program: pill === 'in-program' ? true : undefined,
    ordering: ordering || undefined,
    page,
    page_size: PAGE_SIZE,
  }

  const query = useV1RspoInstitutionsList(queryParams)
  const data = query.data?.status === 200 ? query.data.data : undefined

  const resetPage = () => setPage(1)

  const columns: Column<RspoInstitutionList>[] = [
    {
      header: 'Nazwa',
      sortKey: 'name',
      cell: (row) => {
        const subtitle = hierarchySubtitle(row)
        const marker = zsckrMarker(row)
        return (
          <div className="flex flex-col">
            <span className="flex items-center gap-2">
              <span className="font-medium">{row.name}</span>
              {marker ? (
                <StatusBadge tone={marker.tone}>
                  <Wheat className="size-3" />
                  {marker.label}
                </StatusBadge>
              ) : null}
              {row.school ? <StatusBadge tone="outline">w programie</StatusBadge> : null}
            </span>
            {subtitle ? <span className="text-xs text-muted-foreground">{subtitle}</span> : null}
          </div>
        )
      },
    },
    {
      header: 'RSPO',
      sortKey: 'rspo',
      cell: (row) => <span className="tabular-nums">{row.rspo}</span>,
    },
    {
      header: 'Typ',
      sortKey: 'type',
      cell: (row) => <span className="text-muted-foreground">{row.type ?? '—'}</span>,
    },
    {
      header: 'Województwo',
      sortKey: 'address_voivodeship',
      cell: (row) => (
        <span className="text-muted-foreground">{row.address_voivodeship ?? '—'}</span>
      ),
    },
    {
      header: 'Powiat',
      cell: (row) => <span className="text-muted-foreground">{row.address_district ?? '—'}</span>,
    },
    {
      header: 'Miejscowość',
      cell: (row) => <span className="text-muted-foreground">{row.address_locality ?? '—'}</span>,
    },
    {
      header: 'Uczniowie',
      className: 'text-right',
      cell: (row) => (
        <span className="tabular-nums text-muted-foreground">{row.student_count ?? '—'}</span>
      ),
    },
    {
      header: 'Status publiczny',
      cell: (row) => <span className="text-muted-foreground">{row.public_status ?? '—'}</span>,
    },
  ]

  const filters = (
    <>
      <Select
        value={voivodeship}
        onValueChange={(v) => {
          setVoivodeship(v)
          resetPage()
        }}
      >
        <SelectTrigger className="h-8 w-44">
          <SelectValue placeholder="Województwo" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>Wszystkie województwa</SelectItem>
          {rspoVoivodeshipOptions.map(([value, label]) => (
            <SelectItem key={value} value={value}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        value={type}
        onChange={(e) => {
          setType(e.target.value)
          resetPage()
        }}
        placeholder="Typ placówki"
        className="h-8 w-40"
      />
    </>
  )

  return (
    <>
      <PageHeader title="Katalog RSPO" />
      <div className="p-6">
        <DataTable
          columns={columns}
          rows={data?.results ?? []}
          rowKey={(row) => row.rspo}
          count={data?.count ?? 0}
          page={page}
          onPageChange={setPage}
          pageSize={PAGE_SIZE}
          search={search}
          onSearchChange={(v) => {
            setSearch(v)
            setPage(1)
          }}
          searchPlaceholder="Szukaj po nazwie / RSPO / REGON / NIP…"
          stateValue={pill}
          stateOptions={pillOptions}
          onStateChange={(v) => {
            setPill(v)
            setPage(1)
          }}
          filters={filters}
          ordering={ordering}
          onOrderingChange={(o) => {
            setOrdering(o)
            setPage(1)
          }}
          isLoading={query.isLoading}
          isError={queryHasHttpError(query)}
          onRetry={() => void query.refetch()}
          emptyMessage={rspoEmptyMessage(search, pill, voivodeship !== ALL, type !== '')}
          onRowClick={(row) =>
            navigate({ to: '/katalog-rspo/$rspoId', params: { rspoId: String(row.rspo) } })
          }
          countNoun={['placówka', 'placówek']}
        />
      </div>
    </>
  )
}
